<script setup>
import appconfig from "@/utils/config_demo"; //常用方法
import { ElMessage, TabsInstance, ElCarousel } from "element-plus";
import { inject, reactive, ref, watch, onMounted, defineAsyncComponent,nextTick } from "vue";
import { isNull, post, Request, navigateTo } from "@/api/common"; //常用方法
import { useI18n } from "vue-i18n";
import { useScroll } from '@vueuse/core'
//import searchPage from "./hospital/searchpage";
import pageInfoShow from "./base/pageInfoShow";


import bottomTab from "./common/bottomTab";
import { RouterLink,useRoute } from 'vue-router'
import { computed } from 'vue'
import { ChatDotRound } from '@element-plus/icons-vue'

const dialogVisible = ref(false)
const message = ref('')
const response = ref('')
const loading = ref(false)
const error = ref(null)
const history = ref([]) // 对话历史记录
const currentResponse = ref('') // 用于存储当前正在生成的回复
const chatHistory = ref(null) // 用于操作聊天历史区域的滚动

// 从 localStorage 加载历史记录
onMounted(() => {
  const savedHistory = localStorage.getItem('chatHistory')
  if (savedHistory) {
    history.value = JSON.parse(savedHistory)
  }
})

// 保存历史记录到 localStorage
const saveHistory = () => {
  localStorage.setItem('chatHistory', JSON.stringify(history.value))
}

const handleCloseDialog = (done) => {
  message.value = ''
  response.value = ''
  error.value = null
  done()
}

// 滚动到底部
const scrollToBottom = () => {
  if (chatHistory.value) {
    setTimeout(() => {
      chatHistory.value.scrollTop = chatHistory.value.scrollHeight
    }, 0)
  }
}

const handleSend = async () => {
  if (!message.value.trim()) return

  const userMessage = message.value
  message.value = '' // 立即清空输入框
  
  // 立即添加用户消息到历史记录
  history.value.push({ role: 'user', content: userMessage })
  scrollToBottom()

  loading.value = true
  error.value = null
  currentResponse.value = ''

  // 添加一个临时的助手消息用于流式显示
  history.value.push({ role: 'assistant', content: '' })
  
  try {
    const options = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.VUE_APP_SILICONFLOW_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'deepseek-ai/DeepSeek-V3',
        messages: [
          ...history.value.slice(0, -1), // 不包含临时消息
          { role: 'user', content: userMessage }
        ],
        stream: true,
        max_tokens: 512,
        temperature: 0.7,
        top_p: 0.7,
        response_format: { type: 'text' }
      })
    }

    const res = await fetch('/api/v1/chat/completions', options)

    if (!res.ok) {
      throw new Error(`请求失败: ${res.statusText}`)
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder('utf-8')

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      const lines = chunk.split('\n').filter(line => line.trim() !== '')

      for (const line of lines) {
        const message = line.replace(/^data: /, '')
        if (message === '[DONE]') break

        try {
          const parsed = JSON.parse(message)
          if (parsed.choices && parsed.choices.length > 0) {
            const content = parsed.choices[0].delta.content || ''
            currentResponse.value += content
            // 实时更新最后一条消息的内容
            history.value[history.value.length - 1].content = currentResponse.value
            scrollToBottom()
          }
        } catch (e) {
          console.error('解析响应失败:', e)
        }
      }
    }

    // 保存到 localStorage
    saveHistory()
    
  } catch (err) {
    console.error('API请求失败:', err)
    error.value = '请求失败，请稍后重试'
    // 移除临时消息
    history.value.pop()
  } finally {
    loading.value = false
  }
}

// 清除所有对话
const clearChat = () => {
  message.value = ''
  response.value = ''
  error.value = null
  history.value = []
  localStorage.removeItem('chatHistory')
}

// 处理回车键发送
const handleKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

const route = useRoute()
const { y } = useScroll(window)

// 路由路径与名称的映射
const routeNameMap = {
    '/': '首页',
    '/checkup': '智能体检',
    '/product': '产品中心',
    '/consult': '售前咨询',
    '/cooperate': '商务合作',
    '/medicalplatform': '医疗综合服务平台'
}
// 当前视图状态
const currentView = ref('Home')
// 分离组件加载器和异步组件
const componentLoaders = {
  Home: () => import('@/views/website/home.vue'),
  Product: () => import('@/views/website/product.vue'),
  Checkup: () => import('@/views/website/checkup.vue'),
  Cooperate: () => import('@/views/website/cooperate.vue'),
  Consult: () => import('@/views/website/consult.vue'),
  Medicalplatform: () => import('@/views/website/medicalplatform.vue'),
}

// 创建异步组件引用
const components = {
  Home: defineAsyncComponent(componentLoaders.Home),
  Product: defineAsyncComponent(componentLoaders.Product),
  Checkup: defineAsyncComponent(componentLoaders.Checkup),
  Cooperate: defineAsyncComponent(componentLoaders.Cooperate),
  Consult: defineAsyncComponent(componentLoaders.Consult),
  Medicalplatform: defineAsyncComponent(componentLoaders.Medicalplatform),
}
// 动态组件加载
// const components = {
//   Home: defineAsyncComponent(() => 
//     import('@/views/website/home.vue')
//   ),
//   Product: defineAsyncComponent(() => 
//     import('@/views/website/product.vue')
//   ),
//   Checkup: defineAsyncComponent(() => 
//     import('@/views/website/checkup.vue')
//   ),
//   Cooperate: defineAsyncComponent(() =>
//     import('@/views/website/cooperate.vue')
//   ),
//   Consult: defineAsyncComponent(() =>
//     import('@/views/website/consult.vue')
//   ),
// }

// 增强的视图切换方法
const changeView = async (viewName) => {
  try {
    // 先加载组件
    await componentLoaders[viewName]()
    // 再切换视图
    currentView.value = viewName
    // window.history.replaceState({ view: viewName }, '', `?view=${viewName}`)
    await nextTick()
    window.scrollTo(0, 0)
  } catch (error) {
    console.error('组件加载失败:', error)
    // 可以跳转到错误页面
    currentView.value = 'Error' // 需要定义Error组件
  }
}

// 视图名称映射
const viewNames = {
  Home: '首页',
  Product: '产品介绍',
  Checkup: '体检系统',
  Cooperate: '商务合作',
  Consult: '联系我们',
  Medicalplatform: '医疗综合服务平台'
}

// 当前视图名称计算属性
const currentViewName = computed(() => {
  return viewNames[currentView.value] || '未知页面'
})


// 当前组件计算属性
const currentComponent = computed(() => components[currentView.value])

// 计算是否需要显示面包屑
const showBreadcrumb = computed(() => currentView.value !== 'Home')
// 处理面包屑点击
const handleBreadcrumbClick = (targetView) => {
  if (currentView.value === targetView) return
  currentView.value = targetView
}
const currentRoute = computed(() => route.path !== '/')

const getBreadcrumbName = computed(() => {
    return routeNameMap[route.path] || route.path
})

const carouselRef = ref(null);
const { locale } = useI18n();
// 获取全局对象`
// 获取全局对象`
const global = inject("global");
let carouselHeight = ref(200); //总记录数
const pageInfoShowRef = ref(null);
let ownerType = ref(""); //查询关键字
let scrHeight = ref(400); //总记录数
let datas = ref([]);
let carouselItems = ref([]);
let menuItems = ref([]);
let _LoginUser = reactive({language:"ft"}); //当前用户
onMounted(() => {
  window.addEventListener("resize", handleResize);
  var width = window.screen.width;
  
  carouselHeight.value = 500*width/1920;
  //获取菜单
  //scrHeight.value = window.screen.height - 85;

  /*if (!isNull(global.store.state.userInfo)) {
    //判断下是否有登录
    _LoginUser = global.store.state.userInfo;
    if(Request("openId")!=null&&_LoginUser.openId!= Request("openId")){
      Login();
    }else{
      RetrieveMpModule();
    }
  }else{
    Login();
  }*/
 var lang=localStorage.getItem('lang');
 if(lang==null) lang="ft";
 if(Request("openId")!=null&&_LoginUser.openId!= Request("openId")){
      Login();
    }else{
      RetrieveMpModule();
    }
 _LoginUser.language=lang;
 // RetrieveMpModule();
  //ElMessage(scrHeight.value.toString());
});
function handleResize() {
  var width = window.screen.width;
  
  carouselHeight.value = 500*width/1920;
 
}

const Login = function () {
  var param = new Object();
  param.orgCode = appconfig.orgCode;
  param.openId = Request("openId");
  if (isNull(param.openId)) {
    global.router.push({ path: "/login" });
  }
  ElMessage("链接合法性校验中...");
  post(
    "sys/GetUserInfoByOpenId",
    param,
    function (result) {
        _LoginUser=result.data;
     // ElMessage("Authorize Sucess,Loading datas");
      global.store.commit('setUserInfo', result.data); //保存用户信息
      localStorage.setItem("lang", result.data.language);
      // 设置本地存储数据 localStorage.setItem('key', 'value');np
      localStorage.setItem("LoginUser", JSON.stringify(result.data));
      
      RetrieveMpModule();
    },
    function (result) {
      ElMessage("链接合法性校验失败:" + result.msg);
    }
  );
};

const RetrieveMpModule = function () {
  var param = new Object();
  param.orgCode = appconfig.orgCode;
  post("health/GetMyMpModule", param, function (result) {
    datas.value = result.datas;
    result.datas.forEach((element) => {
      if (element.showType == "9") carouselItems.value = element.children;

      if (element.showType == "4") menuItems.value = element.children;

      /* if(element.showType == '91'){ //保存底部导航选项
        localStorage.setItem("BottomTab", JSON.stringify(element));
      }*/
    });
    if (menuItems.value.length > 0) {
      ownerType.value=menuItems.value[0].name;
      pageInfoShowRef.value.setOwnerType(menuItems.value[0].name);
    }
  });
};

const swithLanguage=(lang)=>{ //切换语言
    _LoginUser.language=lang;
    global.store.commit('setUserInfo', _LoginUser);
    locale.value = lang;  
    localStorage.setItem('lang', lang)
localStorage.setItem('LoginUser', JSON.stringify(_LoginUser))
  //保存到数据库

}

const handlerMenuChange = function (key) {
 // if (keyPath.length < 1) return;
  ownerType.value=menuItems.value[key].name;
  pageInfoShowRef.value.setOwnerType(menuItems.value[key].name);
};

let startX;
const handleTouchStart = (event) => {
  startX = event.touches[0].clientX;
};

const handleTouchEnd = (event) => {
  const currentX = event.changedTouches[0].clientX;
  const delta = currentX - startX;

  if (delta > 100) {
    // 向左滑动
    // 执行上滑操作
    //swapeDown();
    //  carouselRef.value.prev();
  } else if (delta < -100) {
    //向右
    // 向上滑动
    //swapeUp();
    //console.log('向上滑动');
    // carouselRef.value.next();
  }
};



</script>
<template>
  <div style=" width:100%;  margin:auto;">
    <div style=" line-height:40px;">
<div class="content PCView" >
  <el-row :gutter="0" >
            <el-col :xs="1" :sm="1" :md="1" :lg="1" :xl="1" @click="switchMenuCollapse">
          
            </el-col>
            <el-col :xs="4" :sm="6" :md="10" :lg="16" :xl="5" class="roleName">
              </el-col
            >
            <el-col :xs="4" :sm="4" :md="4" :lg="1" :xl="4" v-if="msgCount>0">
              <el-icon size="large"><ShoppingCart /></el-icon>
             
            </el-col>
            <el-col :xs="8" :sm="1" :md="1" :lg="2" :xl="1" v-if="_LoginUser.userName==null">
              
              {{$t("登录/注册")}}
            </el-col>
            <el-col :xs="8" :sm="1" :md="1" :lg="2" :xl="1"  >
              |  {{$t("我的订单")}}|</el-col>
            <el-col :xs="8" :sm="4" :md="4" :lg="3" :xl="1" >
               
              {{$t("语言")}}：
              <el-dropdown :hide-on-click="false" @command="swithLanguage" style="line-height:40px;">
                <span class="el-dropdown-link">
                  {{_LoginUser.language=="cn"?"简体中文":(_LoginUser.language=="en"?"English":"繁体中文")}}

                  <el-icon class="el-icon--right"><arrow-down /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item  command="cn">
                      <span class="pf-opt-name">简体中文</span>
                    </el-dropdown-item>

                    <el-dropdown-item command="en">
                      <span class="pf-opt-name">English</span>
                    </el-dropdown-item>
                    <el-dropdown-item command="big">
                      <span class="pf-opt-name">繁体中文</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown></el-col
            >
            <el-col :xs="8" :sm="5" :md="5" :lg="4" :xl="2" v-if="_LoginUser.userName!=null">
              <el-dropdown :hide-on-click="false">
                <span class="el-dropdown-link">
                  <el-icon size="large"><UserFilled /></el-icon>

                  <span class="javascript:;"
                    >
                    {{ $t("您好") }}
                   
                    ！{{ _LoginUser.userName }}
                  </span>

                  <el-icon class="el-icon--right"><arrow-down /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item>
                      <el-icon><User /></el-icon>
                      <span class="pf-opt-name">{{ $t("个人信息") }}</span>
                    </el-dropdown-item>

                   
                    <el-dropdown-item>
                      <el-icon><Lock /></el-icon>
                      <span class="pf-opt-name">修改密码</span>
                    </el-dropdown-item>
                    <el-dropdown-item @click="logout">
                      <el-icon><SwitchButton /></el-icon>

                      <span class="pf-opt-name">退出</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown></el-col
            >
          </el-row>
        
          
          </div>
          <!-- 导航栏-->
          <header class='app-header'>
            <div class="container">
      <h1 class="logo">
        
        <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Home' }"
              @click="changeView('Home')"
            >天唯士</a>
      </h1>
      <ul class="app-header-nav">
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Home' }"
              @click="changeView('Home')"
            >首页</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Product' }"
              @click="changeView('Product')"
            >产品介绍</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Checkup' }"
              @click="changeView('Checkup')"
            >体检系统</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Medicalplatform' }"
              @click="changeView('Medicalplatform')"
            >医疗综合服务平台</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Cooperate' }"
              @click="changeView('Cooperate')"
            >商务合作</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Consult' }"
              @click="changeView('Consult')"
            >联系我们</a>
        </li>
      </ul>
      
      <!-- 头部购物车 -->
      
            </div>
          </header>
          <!-- 悬浮导航栏 -->
          <div class="app-header-sticky" :class="{ show: y > 78 }">
    <div class="container">
      <RouterLink class="logo" to="/" />
      
      <!-- 导航区域 -->
      <ul class="app-header-nav">
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Home' }"
              @click="changeView('Home')"
            >首页</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Product' }"
              @click="changeView('Product')"
            >产品介绍</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Checkup' }"
              @click="changeView('Checkup')"
            >体检系统</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Medicalplatform' }"
              @click="changeView('Medicalplatform')"
            >医疗综合服务平台</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Cooperate' }"
              @click="changeView('Cooperate')"
            >商务合作</a>
        </li>
        <li>
          <a 
              href="javascript:;" 
              :class="{ active: currentView === 'Consult' }"
              @click="changeView('Consult')"
            >联系我们</a>
        </li>
      </ul>
    </div>
  </div>
    <el-breadcrumb 
      v-if="currentView !== 'Home'"
      separator="/" 
      class="breadcrumb-container"
    >
      <el-breadcrumb-item 
        class="cursor-pointer"
        @click="handleBreadcrumbClick('Home')"
      >
        首页
      </el-breadcrumb-item>
      <el-breadcrumb-item>
        {{ currentViewName }}
      </el-breadcrumb-item>
    </el-breadcrumb>
        </div>
 <!-- 内容出口 -->
 
 <main  class="content-container" >
      
    <component 
      :is="components[currentView]" 
      :current-view="currentView"
      @change-view="changeView"
    />
 </main>

</div>
<!-- AI -->
 <div class="ai-assistant">
    <el-tooltip content="智能助手" placement="left">
      <el-button
        type="primary"
        :icon="ChatDotRound"
        circle
        class="ai-icon"
        @click="dialogVisible = true"
      />
    </el-tooltip>

    <el-dialog
      v-model="dialogVisible"
      title="智能助手"
      width="600px"
      :modal-class="'ai-dialog'"
      :lock-scroll="false"
      :before-close="handleCloseDialog"
    >
      <div class="chat-container">
        <div class="chat-history" ref="chatHistory">
          <div v-for="(item, index) in history" :key="index" class="message">
            <div :class="['message-content', item.role === 'user' ? 'user' : 'assistant']">
              <strong>{{ item.role === 'user' ? '你' : '助手' }}:</strong>
              <p>{{ item.content }}<span v-if="loading && index === history.length - 1" class="typing">...</span></p>
            </div>
          </div>
        </div>

        <!-- 显示错误信息 -->
        <div v-if="error" class="error-message">
          <el-alert :title="error" type="error" />
        </div>

        <!-- 输入区域 -->
        <div class="input-area">
          <el-input
            v-model="message"
            type="textarea"
            :rows="3"
            placeholder="请输入您的问题... (按Enter发送)"
            @keydown="handleKeydown"
          />

          <div class="actions">
            <el-button @click="clearChat">清除对话</el-button>
            <el-button
              type="primary"
              :loading="loading"
              @click="handleSend"
            >
              发送
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
<!--底部-->
<footer class="app_footer">
      <!-- 联系我们 -->
      <div class="contact">
        <div class="container">
          <div class="content-wrapper">
            <dl>
              <dt>客户服务</dt>
              <dd @click="dialogVisible = true"><i class="iconfont icon-kefu"></i> 在线客服</dd>
              <dd><i class="iconfont icon-wenti"></i> 问题反馈</dd>
            </dl>
            <dl>
              <dt>关注我们</dt>
              <dd><i class="iconfont icon-weixin"></i> 公众号</dd>
              <dd><i class="iconfont icon-weibo"></i> 微博</dd>
            </dl>
            <dl>
              <dt>查看小程序</dt>
              <dd class="qrcode"><img src="@/assets/images/小程序.jpg" /></dd>
              <dd class="download">
                <span>扫描二维码</span>
                <span>查看详情</span>
                <a href="#小程序://天唯小筑/HbKoEuiD4L8azIx">程序链接</a>
              </dd>
            </dl>
            <dl>
              <dt>服务热线</dt>
                <dd class="hotline">
                  <div class="new-number">0592-5971709</div>
                  18030118313
                  <small>周一至周日 8:00-18:00</small>
                </dd>
            </dl>
          </div>
        </div>
      </div>
      <!-- 其它 -->
      <div class="extra">
        <div class="container">
          <div class="copyright">
            <p>
              <a href="javascript:;">关于我们</a>
              <a href="javascript:;">帮助中心</a>
              <a href="javascript:;">法律声明</a>
              <a href="javascript:;">服务条款</a>
              <a href="javascript:;">商务合作</a>
              <a href="javascript:;">搜索推荐</a>
              <a href="javascript:;">友情链接</a>
            </p>
            <p>COPYRIGHT  ©  2009-2016teamwish 版权所有</p>
          </div>
        </div>
      </div>
    </footer>
</template>
  
  
  
  

  
  <style scoped lang="scss">
  .content a{
    color:#555;
    text-decoration: none;
  }
.menu-type {
  clear: both;
  margin-bottom: 10px;
  background-color: #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.col-2-item:nth-child(even) {
  border-left: 1px solid #ccc;
  width: calc(50% - 2px);
}

.nav-tabs li {
  float: left;
  margin-right: 4px;
}
.ft{
  padding-top: 20px;
  text-align: left;
  line-height: 30px;
}

/* pc */
@media only screen and (min-width: 600px)  {
  .col-2-item{
        font-size: 32px;
        line-height:100px;
        text-align: center;
}
.col-2-item icon{
 font-size: 60px;

}
.content{
  width: 1280px;
  margin: auto
}
.col-22-item .title{
 line-height:50px;
 font-size: 32px;
 
}
.col-25-item .title{
 line-height:50px;
 font-size: 32px;
 
}
.col-25-item img{
 width:300px;
}
.col-25-item .name{
 line-height:40px;
 font-size: 20px;
 margin-bottom:20px;
 background-color:#eee;
}
}

  /*phone*/
@media only screen and (min-width: 0px) and (max-width: 420px) {
	.col-2-item{
        font-size: 16px;
        line-height:50px;
        text-align: center;
}
.col-2-item icon{
 font-size: 32px;

}
.col-4-item{
        font-size: 16px;
        text-align: center;
}
.col-4-item icon{
 font-size: 32px;
}
.col-25-item img{
 width:160px;
}
}

/*新样式*/
.app-header {
  background: #fff;

  .container {
    display: flex;
    align-items: center;
  }

  .logo {
    width: 300px;
    padding-left: 100px;

    a {
      display: block;
      height: 132px;
      width: 100%;
      text-indent: -9999px;
      background: url('@/assets/images/twslogo2.png') no-repeat center 53px / contain;
    }
  }

  .app-header-nav {
    width: 1000px;
    display: flex;
    padding-left: 40px;
    position: relative;
    z-index: 998;
  
    li {
      margin-right: 5px;
      width: 130px;
      text-align: center;
  
      a {
        font-size: 16px;
        line-height: 32px;
        height: 32px;
        display: inline-block;
        position: relative;
  
        &:hover {
          color: #27ba9b;
          &::after {
            width: 100%;
          }
        }

        &::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 0;
          height: 2px;
          background: #27ba9b;
          transition: all 0.3s;
        }
      }
  
      .active {
        color: #27ba9b;
        &::after {
          width: 100%;
        }
      }
    }
  }

  .search {
    width: 170px;
    height: 32px;
    position: relative;
    border-bottom: 1px solid #e7e7e7;
    line-height: 32px;

    .icon-search {
      font-size: 18px;
      margin-left: 5px;
    }

    input {
      width: 140px;
      padding-left: 5px;
      color: #666;
    }
  }

  .cart {
    width: 50px;

    .curr {
      height: 32px;
      line-height: 32px;
      text-align: center;
      position: relative;
      display: block;

      .icon-cart {
        font-size: 22px;
      }

      em {
        font-style: normal;
        position: absolute;
        right: 0;
        top: 0;
        padding: 1px 6px;
        line-height: 1;
        background: #e26237;
        color: #fff;
        font-size: 12px;
        border-radius: 10px;
        font-family: Arial;
      }
    }
  }
}



/* 底部样式 */
.app_footer {
    overflow: hidden;
    background-color: #f5f5f5;
    padding-top: 20px;
  
    .contact {
      background: #fff;
  
      .container {
        padding: 60px 0 40px 25px;
        display: flex;
        justify-content: center;
  
        .content-wrapper {
          display: flex;
          justify-content: center;
          gap: 40px;
        }
      }
  
      dl {
        height: 190px;
        text-align: center;
        padding: 0 30px;
        border-right: 1px solid #f2f2f2;
        color: #999;
  
        &:first-child {
          padding-left: 0;
        }
  
        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      }
  
      dt {
        line-height: 1;
        font-size: 18px;
      }
  
      dd {
        margin: 36px 12px 0 0;
        float: left;
        width: 92px;
        height: 92px;
        padding-top: 10px;
        border: 1px solid #ededed;
  
        .iconfont {
          font-size: 36px;
          display: block;
          color: #666;
        }
  
        &:hover {
          .iconfont {
            color: #27ba9b;
          }
        }
  
        &:last-child {
          margin-right: 0;
        }
      }
  
      .qrcode {
        width: 92px;
        height: 92px;
        padding: 7px;
        border: 1px solid #ededed;
      }
  
      .download {
        padding-top: 5px;
        font-size: 14px;
        width: auto;
        height: auto;
        border: none;
  
        span {
          display: block;
        }
  
        a {
          display: block;
          line-height: 1;
          padding: 10px 25px;
          margin-top: 5px;
          color: #fff;
          border-radius: 2px;
          background-color: #27ba9b;
        }
      }
  
      .hotline {
        padding-top: 10px; /* 减少顶部间距 */
        border: none;
        width: 200px;
        font-size: 20px;
        color: #666;
        display: flex;
        flex-direction: column; /* 垂直排列子元素 */

        .new-number {
            font-size: 18px; /* 可选，根据设计调整 */
            color: #666;
            margin-bottom: 5px; /* 与下方号码保持间距 */
        }
      
        small {
            display: block;
            font-size: 15px;
            color: #999;
            margin-top: 5px; /* 与上方号码保持间距 */
        }
      }
    }
  
    .extra {
      background-color: #333;
  
      .container {
        display: flex;
        justify-content: center;
      }
    }
  
    .slogan {
      height: 178px;
      line-height: 58px;
      padding: 60px 100px;
      border-bottom: 1px solid #434343;
      display: flex;
      justify-content: space-between;
  
      a {
        height: 58px;
        line-height: 58px;
        color: #fff;
        font-size: 28px;
  
        i {
          font-size: 50px;
          vertical-align: middle;
          margin-right: 10px;
          font-weight: 100;
        }
  
        span {
          vertical-align: middle;
          text-shadow: 0 0 1px #333;
        }
      }
    }
  
    .copyright {
      height: 170px;
      padding-top: 40px;
      text-align: center;
      color: #999;
      font-size: 15px;
  
      p {
        line-height: 1;
        margin-bottom: 20px;
      }
  
      a {
        color: #999;
        line-height: 1;
        padding: 0 10px;
        border-right: 1px solid #999;
  
        &:last-child {
          border-right: none;
        }
      }
    }
  }

/* 面包屑导航*/
.breadcrumb-container {
  background: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding-left: 150px;
  padding-bottom: 20px;
}

/* 调整内容区域间距 */
main {
  padding-top: 0; /* 当面包屑隐藏时消除顶部间距 */
}

.cursor-pointer {
  cursor: pointer;
  &:hover {
    color: #1890ff;
  }
}

/* 禁用最后一项的hover效果 */
.el-breadcrumb__item:last-child .cursor-pointer {
  cursor: default;
  &:hover {
    color: inherit;
  }
}
// 悬浮导航栏
.app-header-sticky {
  width: 100%;
  height: 80px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 999;
  background-color: #fff;
  border-bottom: 1px solid #e4e4e4;
  // 此处为关键样式!!!
  // 状态一：往上平移自身高度 + 完全透明
  transform: translateY(-100%);
  opacity: 0;

  // 状态二：移除平移 + 完全不透明
  &.show {
    transition: all 0.3s linear;
    transform: none;
    opacity: 1;
  }

  .container {
    display: flex;
    align-items: center;
  }

  .logo {
    width: 200px;
    height: 80px;
    background: url("@/assets/images/twslogo2.png") no-repeat right 28px;
    background-size: 160px auto;
  }

  .right {
    width: 260px;
    display: flex;
    align-items: center;
    padding-left: 40px;
    border-left: 2px solid #27ba9b;

    .user-info {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: 0 10px;
      border-radius: 4px;
      transition: all 0.3s;

      &:hover {
        background: #f5f7fa;
      }

      .username {
        margin-left: 8px;
        font-size: 14px;
        color: #333;
      }
    }
  }
}

.app-header-nav {
  width: 1000px;
  display: flex;
  padding-left: 40px;
  position: relative;
  z-index: 998;

  li {
    margin-right: 5px;
    width: 130px;
    text-align: center;

    a {
      font-size: 16px;
      line-height: 32px;
      height: 32px;
      display: inline-block;
      position: relative;

      &:hover {
        color: #27ba9b;
        &::after {
          width: 100%;
        }
      }

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 0;
        height: 2px;
        background: #27ba9b;
        transition: all 0.3s;
      }
    }

    .active {
      color: #27ba9b;
      &::after {
        width: 100%;
      }
    }
  }
}

:deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  
  .el-icon {
    margin-right: 8px;
    font-size: 16px;
  }
}

:deep(.el-button) {
  padding: 8px 20px;
}

:deep(.el-avatar) {
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
// AI
.ai-assistant {
  position: fixed;
  right: 40px;
  bottom: 40px;
  z-index: 9999;
}

.ai-icon {
  width: 56px;
  height: 56px;
  font-size: 24px;
  transition: transform 0.3s ease;
}

/* 添加全局样式 */
:global(.ai-dialog) {
  overflow: hidden;
}

:global(.el-overlay) {
  overflow: hidden;
}

/* 确保对话框内容可以滚动 */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 500px;
  overflow: hidden;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  margin-bottom: 20px;
  scroll-behavior: smooth; /* 添加平滑滚动效果 */
}

.message {
  margin-bottom: 20px;
}

.message-content {
  padding: 12px;
  border-radius: 8px;
  max-width: 80%;
  word-break: break-word;
}

.message-content.user {
  background: #e1f3d8;
  margin-left: auto;
}

.message-content.assistant {
  background: #f5f7fa;
}

.message-content strong {
  display: block;
  margin-bottom: 5px;
  color: #333;
}

.input-area {
  padding: 20px;
  background: #fff;
  border-top: 1px solid #eee;
}

.actions {
  margin-top: 12px;
  text-align: right;
}

.error-message {
  margin: 10px 20px;
}

.typing {
  display: inline-block;
  width: 12px;
  animation: typing 1s infinite;
}

@keyframes typing {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 0.8; }
}

.message-content p {
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
}
.el-button.is-circle {
  width: 55px;
}
</style>
